import React, { useRef } from "react";

const OPEN_MS = 280;
const CLOSE_MS = 200;
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * One question. Still a real <details>/<summary>, driven imperatively.
 *
 * The element is left uncontrolled on purpose: React setting `open` while the
 * browser also toggles it on click gives two owners for one piece of state,
 * and they disagree the moment an animation is mid-flight. Refs sidestep it,
 * and the markup react-snap serialises is unchanged.
 *
 * Everything here is an enhancement over behaviour that already works. With
 * no JavaScript -- or with reduced motion asked for -- the click falls
 * through to the native instant toggle.
 */
function FaqItem({ faq, defaultOpen }) {
  const detailsRef = useRef(null);
  const contentRef = useRef(null);
  const animationRef = useRef(null);

  const handleClick = (event) => {
    const details = detailsRef.current;
    const content = contentRef.current;
    if (!details || !content || typeof content.animate !== "function") return;
    if (prefersReducedMotion()) return;

    // Take over from the browser: it would otherwise show or hide the panel
    // instantly, leaving nothing to animate.
    event.preventDefault();

    if (animationRef.current) {
      animationRef.current.cancel();
      animationRef.current = null;
    }

    const finish = () => {
      content.style.height = "";
      content.style.opacity = "";
      animationRef.current = null;
    };

    if (details.open) {
      const from = content.offsetHeight;
      const animation = content.animate(
        [
          { height: `${from}px`, opacity: 1 },
          { height: "0px", opacity: 0 },
        ],
        { duration: CLOSE_MS, easing: "ease-in" }
      );
      animationRef.current = animation;
      animation.onfinish = () => {
        // Closed only once it has finished shrinking, or the content would
        // vanish on the first frame.
        details.open = false;
        finish();
      };
      return;
    }

    // Opened first so the panel has a height to measure, then animated from
    // nothing up to it.
    details.open = true;
    const to = content.offsetHeight;
    const animation = content.animate(
      [
        { height: "0px", opacity: 0 },
        { height: `${to}px`, opacity: 1 },
      ],
      { duration: OPEN_MS, easing: EASE }
    );
    animationRef.current = animation;
    animation.onfinish = finish;
  };

  return (
    <details ref={detailsRef} open={defaultOpen} className="group py-4 sm:py-5">
      <summary
        onClick={handleClick}
        className="flex cursor-pointer list-none items-start justify-between gap-4 text-[#111827] font-semibold text-[16px] sm:text-[17px] md:text-[18px] leading-relaxed marker:hidden [&::-webkit-details-marker]:hidden"
      >
        <span>{faq.question}</span>
        <span
          aria-hidden="true"
          className="mt-1 shrink-0 text-[#117816] transition-transform duration-200 group-open:rotate-45"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M9 3.75v10.5M3.75 9h10.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </summary>

      {/* The wrapper is what gets animated; the padding lives inside it so the
          height being tweened includes it and the panel does not jump at the
          end of the transition. */}
      <div ref={contentRef} className="faq-answer-wrap">
        <div
          className="faq-answer pt-3 text-[#4b5563] text-[15px] sm:text-[16px] md:text-[17px]"
          dangerouslySetInnerHTML={{ __html: faq.answerHtml }}
        />
      </div>
    </details>
  );
}

// Native <details>/<summary> rather than useState for the open state: it is
// keyboard operable, announced correctly by screen readers, and open/close
// works in the react-snap snapshot before any JavaScript loads. A hand-rolled
// accordion would need all of that written by hand and would collapse to
// nothing in the prerendered HTML.
const BlogFaq = ({ faqs, id = "faq" }) => {
  if (!faqs?.length) return null;

  return (
    // Width and gutters come from the column this sits in, so the FAQ card
    // lines up with the article above it at every breakpoint.
    <section id={id} aria-labelledby={`${id}-heading`} className="mt-8">
      <div>
        <div className="bg-white rounded-[28px] border border-[#edf1ed] shadow-sm p-6 sm:p-10 md:p-14">
          <h2
            id={`${id}-heading`}
            className="text-[26px] md:text-[32px] font-bold text-[#111827] leading-tight mb-2"
          >
            Frequently Asked Questions
          </h2>
          <p className="text-[#6b7280] text-[15px] md:text-[16px] mb-8">
            Quick answers to the questions we hear most often.
          </p>

          <div className="divide-y divide-[#eef2ee] border-t border-[#eef2ee]">
            {faqs.map((faq, i) => (
              // The first one is open so the section does not read as an
              // empty list of bars on arrival.
              <FaqItem key={faq.question} faq={faq} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogFaq;
